import { EDITOR_MARKETING_PATHS } from "@/lib/marketing/editorContent";

export type MarketingLink = { href: string; label: string };
export type MarketingFooterColumn = { title: string; links: MarketingLink[] };

/** Canonical feature catalog: dedicated landing pages for major surfaces. */
export type MarketingFeature = {
  href: string;
  label: string;
  blurb: string;
};

/**
 * Every dedicated /features/* landing. The hub lists these plus full tool
 * inventories from productInventory (analysis/outline/characters/publishing/write).
 */
export const MARKETING_FEATURES: MarketingFeature[] = [
  {
    href: "/features/continuity-guardian",
    label: "Continuity Guardian",
    blurb:
      "Audit your manuscript for contradictions across chapters and quote both passages so you can verify each finding.",
  },
  {
    href: "/features/story-bible",
    label: "Story Bible",
    blurb:
      "Suggest characters, places, and world rules from your draft. You approve what becomes canon.",
  },
  {
    href: "/features/character-tracking",
    label: "Character Tracking",
    blurb:
      "Approve character entries and aliases from the manuscript, then catch trait and relationship drift across the full draft.",
  },
  {
    href: "/features/characters",
    label: "Characters Workspace",
    blurb:
      "Dossiers, relationship maps, arcs, and the full Characters tool grid for depth, voice, and craft checks.",
  },
  {
    href: "/features/timeline",
    label: "Timeline",
    blurb:
      "Find timeline contradictions in the manuscript: ages, dates, seasons, and sequence across chapters.",
  },
  {
    href: "/features/outline",
    label: "Outline",
    blurb:
      "Outline board, beat sheet, plot threads, canvas, and structure tools for planning long novels.",
  },
  {
    href: "/features/analysis-tools",
    label: "Analysis Tools",
    blurb:
      "Featured deep reads plus the full searchable analysis catalog across pacing, prose, dialogue, characters, and theme.",
  },
  {
    href: "/features/custom-tools",
    label: "Build Your Own Tools",
    blurb:
      "Describe the check you keep doing by hand and Novilot builds it into a real tool, with its credit cost stated before it runs.",
  },
  {
    href: "/features/manuscript-editor",
    label: "Manuscript Editor",
    blurb:
      "A focused writing surface with chapters, goals, autocomplete, proofread checks, and continuity nudges.",
  },
  {
    href: "/features/copilot",
    label: "Manuscript-aware Copilot",
    blurb:
      "Chat that pulls context from the right scenes and your story bible. Flags and suggestions only. You decide every word.",
  },
  {
    href: "/features/proofread",
    label: "Proofread",
    blurb:
      "In-editor craft checks for filler words, telling, weak tags, passive voice, echoes, and related underlines.",
  },
  {
    href: "/features/publishing",
    label: "Publishing Tools",
    blurb:
      "Query letters, blurbs, genre intelligence, market fit, submission checks, and publish-ready export.",
  },
  {
    href: "/features/beta-readers",
    label: "Beta Reader Hub",
    blurb:
      "Share drafts, collect structured feedback, and synthesize reader reactions into clear action items.",
  },
  {
    href: "/features/series",
    label: "Series",
    blurb:
      "Series bible workspace: group novels, keep shared canon honest, and check continuity across books.",
  },
  {
    href: "/features/import-and-export",
    label: "Import and Export",
    blurb:
      "Bring an existing manuscript in, work in Novilot, and export when you are ready to share or publish elsewhere.",
  },
  {
    href: "/features/cloud-backup",
    label: "Cloud Backup",
    blurb:
      "Back up manuscript, story bible, and outline packages to Dropbox or a private GitHub repo.",
  },
  {
    href: "/features/translation",
    label: "Multilingual Writing",
    blurb:
      "Write in the language that feels natural to you while Novilot keeps the interface, project language, and AI output aligned.",
  },
];

/**
 * Free reference and template pages — the top of the funnel.
 *
 * Each one has to appear in the footer, the sitemap, the public prefixes and
 * the robots allowlist. Listing them once here is what keeps those four in
 * step: a new top-level path that misses MARKETING_PUBLIC_PREFIXES 307s to
 * /sign-in, and a page nothing links to is a page Google discounts.
 */
export const MARKETING_RESOURCE_PAGES: MarketingLink[] = [
  { href: "/story-bible-template", label: "Story bible template (free)" },
  { href: "/novel-outline-template", label: "Novel outline template" },
  { href: "/beat-sheet-template", label: "Beat sheet template" },
  { href: "/character-profile-template", label: "Character profile template" },
  { href: "/world-building-template", label: "World building template" },
  { href: "/novel-word-count", label: "Novel word count by genre" },
  { href: "/word-count-goal-calculator", label: "Word count goal calculator" },
  { href: "/passive-voice-checker", label: "Passive voice checker (free)" },
  { href: "/readability-score-checker", label: "Readability score checker" },
];

/** Footer Features column stays scannable; hub holds the complete inventory. */
export const MARKETING_FOOTER_FEATURE_HREFS = [
  "/features/continuity-guardian",
  "/features/story-bible",
  "/features/character-tracking",
  "/features/outline",
  "/features/analysis-tools",
  "/features/custom-tools",
  "/features/publishing",
  "/features/import-and-export",
  "/features/cloud-backup",
  "/features/translation",
] as const;

export const MARKETING_FOOTER_COLUMNS: MarketingFooterColumn[] = [
  {
    title: "Features",
    links: [
      ...MARKETING_FOOTER_FEATURE_HREFS.map((href) => {
        const feature = MARKETING_FEATURES.find((f) => f.href === href);
        return { href, label: feature?.label ?? href };
      }),
      { href: "/features", label: "More >>" },
    ],
  },
  {
    title: "Compare",
    links: [
      { href: "/compare/novilot-vs-fictionary", label: "vs Fictionary" },
      { href: "/compare/novilot-vs-plotlens", label: "vs PlotLens" },
      { href: "/compare/novilot-and-perfectit", label: "with PerfectIt" },
      { href: "/compare/novilot-vs-sudowrite", label: "vs Sudowrite" },
      { href: "/compare/novilot-vs-novelcrafter", label: "vs NovelCrafter" },
      { href: "/compare/novilot-vs-scrivener", label: "vs Scrivener" },
      { href: "/compare/novilot-vs-chatgpt-continuity", label: "vs ChatGPT for continuity" },
      { href: "/compare/novilot-vs-manual-story-bible", label: "vs a manual story bible" },
      { href: "/best/continuity-checkers", label: "Best continuity checkers" },
      { href: "/alternatives/scrivener", label: "Scrivener alternative" },
      { href: "/alternatives/sudowrite", label: "Sudowrite alternative" },
    ],
  },
  {
    title: "Guides",
    links: [
      { href: "/how-to", label: "Getting started in Novilot" },
      { href: "/how-to/complete-your-first-client-review", label: "First client review" },
      ...MARKETING_RESOURCE_PAGES,
      { href: "/how-to/start-writing-a-novel-in-novilot", label: "Start writing in Novilot" },
      { href: "/how-to/navigate-novilot", label: "Navigate Novilot" },
      { href: "/how-to/add-screens-and-tools", label: "Add screens and tools" },
      { href: "/how-to/pin-tools-in-novilot", label: "Pin tools" },
      { href: "/how-to/search-your-manuscript", label: "Search your manuscript" },
      { href: "/how-to/create-a-custom-tool", label: "Create a custom tool" },
      { href: "/how-to/organize-a-novel", label: "Organize a novel" },
      { href: "/how-to/track-characters-in-a-novel", label: "Keep track of characters" },
      { href: "/how-to/keep-characters-consistent", label: "Keep characters consistent" },
      { href: "/how-to/create-a-series-bible", label: "Create a series bible" },
      { href: "/how-to/get-feedback-on-your-novel", label: "Get novel feedback" },
    ],
  },
  {
    title: "Audiences",
    links: [
      { href: "/for/fiction-writers", label: "Fiction Writers" },
      { href: "/for/fiction-editors", label: "Fiction Editors" },
      { href: "/for/serial-fiction-writers", label: "Serial Fiction Writers" },
      { href: "/for/fantasy-and-sci-fi", label: "Fantasy and Sci-Fi" },
      { href: "/for/romance-writers", label: "Romance Writers" },
      { href: "/for/indie-authors", label: "Indie Authors" },
    ],
  },
  {
    title: "Company",
    links: [
      { href: "/pricing", label: "Pricing" },
      { href: "/privacy", label: "Privacy" },
      { href: "/terms", label: "Terms" },
      { href: "mailto:support@novilot.com", label: "Contact" },
    ],
  },
];

/** Canonical indexable marketing paths (no old keyword URLs, no /demo). */
export const MARKETING_SITEMAP_PATHS = [
  "/",
  "/features",
  ...MARKETING_FEATURES.map((f) => f.href),
  "/compare/novilot-vs-sudowrite",
  "/compare/novilot-vs-novelcrafter",
  "/compare/novilot-vs-scrivener",
  "/compare/novilot-vs-chatgpt-continuity",
  "/compare/novilot-vs-manual-story-bible",
  ...MARKETING_RESOURCE_PAGES.map((p) => p.href),
  "/how-to",
  "/how-to/start-writing-a-novel-in-novilot",
  "/how-to/navigate-novilot",
  "/how-to/add-screens-and-tools",
  "/how-to/pin-tools-in-novilot",
  "/how-to/search-your-manuscript",
  "/how-to/create-a-custom-tool",
  "/how-to/organize-a-novel",
  "/how-to/track-characters-in-a-novel",
  "/how-to/keep-characters-consistent",
  "/how-to/create-a-series-bible",
  "/how-to/get-feedback-on-your-novel",
  "/best/continuity-checkers",
  "/alternatives/scrivener",
  "/alternatives/sudowrite",
  "/for/serial-fiction-writers",
  "/for/fiction-writers",
  "/for/fantasy-and-sci-fi",
  "/for/romance-writers",
  "/for/indie-authors",
  ...EDITOR_MARKETING_PATHS,
  "/pricing",
  "/faq",
  "/ai",
  "/privacy",
  "/terms",
  "/sign-up",
  "/sign-in",
] as const;

/** Prefixes that must stay public in proxy.ts (exact or path + "/"). */
export const MARKETING_PUBLIC_PREFIXES = [
  ...MARKETING_RESOURCE_PAGES.map((p) => p.href),
  "/features",
  "/compare",
  "/for",
  "/pricing",
  "/how-to",
  "/alternatives",
  "/best",
  "/templates",
  "/security",
  "/sample-continuity-report",
  "/faq",
  "/privacy",
  "/terms",
  "/demo",
] as const;

/** Paths/prefixes robots.ts should explicitly allow for crawlers. */
export const MARKETING_ROBOTS_ALLOW = [
  "/",
  ...MARKETING_RESOURCE_PAGES.map((p) => p.href),
  "/features",
  "/compare",
  "/for",
  "/how-to",
  "/alternatives",
  "/best",
  "/templates",
  "/security",
  "/sample-continuity-report",
  "/faq",
  "/pricing",
  "/privacy",
  "/terms",
  "/sign-in",
  "/sign-up",
  "/read/",
] as const;
